// The pure half of the bell: how its badge spells a count, and which catalog words say what one line
// of the feed is about. The line's subject is read by the server when the feed is; what is decided
// here is only how it is said, in plain text — nothing a server sends is rendered as markup.

import type { NotificationEntry, NotificationSale } from '@/api/notifications/feed'
import { kindCopy } from './preferences'

/** The largest count the badge spells out; anything above it reads `99+`. */
export const BADGE_MAX = 99

/** How many of the newest lines the bell lists — the first page of the feed, newest first. */
export const FEED_PAGE = 20

/** How often the bell asks how many lines are unread, in milliseconds — no push (out of scope). */
export const UNREAD_POLL_MS = 60_000

/** The badge's text for `count` unread lines, or `null` for none, when no badge is drawn. */
export function badgeText(count: number | null | undefined): string | null {
  if (!count || count <= 0) return null
  return count > BADGE_MAX ? `${BADGE_MAX}+` : String(count)
}

/** Whether a line has not been read yet. */
export function isUnread(entry: Pick<NotificationEntry, 'read_at'>): boolean {
  return entry.read_at === null
}

/**
 * The words of one line: a catalog segment under `notifications.lines`, its named parameters, and
 * the count a plural form chooses by, when it has one.
 */
export interface LineCopy {
  key:
    | 'uploadReceived'
    | 'uploadReceivedBy'
    | 'uploadPendingModeration'
    | 'uploadPendingModerationBy'
    | 'membershipRequestReceived'
    | 'membershipRequestsWaiting'
    | 'saleReceived'
    | 'saleReceivedAmount'
    | 'subscriptionReceived'
    | 'subscriptionReceivedAmount'
    | 'contactMessageReceived'
    | 'contactMessageReceivedSubject'
    | 'depositReceived'
    | 'depositReceivedBy'
    | 'depositsReceived'
    | 'depositsReceivedBy'
    | 'unknown'
  params: Record<string, string | number>
  plural?: number
}

/**
 * A sale's total excluding tax, in its own currency, spelled for `locale`; `null` for a sale with
 * no total. A currency `Intl` does not know is spelled as its code.
 */
export function saleAmount(sale: NotificationSale, locale?: string): string | null {
  const cents = sale.total_excl_tax_cents
  if (cents === null) return null
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: sale.currency }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(2)} ${sale.currency}`
  }
}

/**
 * **What one line says**, by its kind and the subject the kind fills: an upload by its title and
 * workspace — and its uploader when the account still carries a name — a request by its applicant,
 * the waiting reminder by the queue's count and the age of its oldest request, a sale by its channel
 * and what was sold — a subscription's first period in words of its own, and the total excluding
 * tax when the sale carries one, spelled for `locale` — and a contact message by its channel, its
 * sender and its subject when it has one. A kind this build does not know, or a line
 * missing the subject its kind fills, says so in general words.
 */
export function lineCopy(entry: NotificationEntry, locale?: string): LineCopy {
  const kind = kindCopy(entry.kind)
  const upload = entry.upload
  if (upload && (kind === 'uploadReceived' || kind === 'uploadPendingModeration')) {
    const params = { title: upload.title, workspace: upload.workspace_name }
    if (upload.uploader_name) {
      return {
        key: kind === 'uploadReceived' ? 'uploadReceivedBy' : 'uploadPendingModerationBy',
        params: { ...params, uploader: upload.uploader_name },
      }
    }
    return { key: kind, params }
  }
  const request = entry.membership_request
  if (request && kind === 'membershipRequestReceived') {
    return { key: kind, params: { name: request.name, email: request.email } }
  }
  const waiting = entry.waiting_requests
  if (waiting && kind === 'membershipRequestsWaiting') {
    return {
      key: kind,
      params: { count: waiting.count, days: waiting.oldest_days },
      plural: waiting.count,
    }
  }
  const sale = entry.sale
  if (sale && kind === 'saleReceived') {
    const params = { channel: sale.channel_name, what: sale.what }
    const amount = saleAmount(sale, locale)
    if (amount === null) return { key: sale.subscription ? 'subscriptionReceived' : 'saleReceived', params }
    return {
      key: sale.subscription ? 'subscriptionReceivedAmount' : 'saleReceivedAmount',
      params: { ...params, amount },
    }
  }
  const message = entry.contact_message
  if (message && kind === 'contactMessageReceived') {
    const params = { channel: message.channel_name, name: message.sender_name }
    if (message.subject) return { key: 'contactMessageReceivedSubject', params: { ...params, subject: message.subject } }
    return { key: kind, params }
  }
  const deposit = entry.deposit
  if (deposit && kind === 'depositReceived') {
    // One line per sending: several files say how many, under the title the member typed.
    const several = deposit.file_count > 1
    const params = { channel: deposit.channel_name, title: deposit.title, count: deposit.file_count }
    if (deposit.sender_name) {
      return { key: several ? 'depositsReceivedBy' : 'depositReceivedBy', params: { ...params, name: deposit.sender_name } }
    }
    return { key: several ? 'depositsReceived' : kind, params }
  }
  return { key: 'unknown', params: {} }
}
